import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, LogOut, TrendingUp, MousePointerClick, ShoppingBag, DollarSign } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { apiClient, formatUsd } from "../lib/api";

const RANGES = [
  { label: "7d", value: 7 },
  { label: "30d", value: 30 },
  { label: "90d", value: 90 },
  { label: "1y", value: 365 },
];

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#F0DFD0] rounded-lg p-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#FDF3ED] flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#B85C56]" strokeWidth={1.5} />
        </div>
        <div className="eyebrow">{label}</div>
      </div>
      <div className="font-serif text-3xl mt-4 text-[#2A1F1F]">{value}</div>
    </div>
  );
}

export default function AdminCampaignsPage() {
  const { admin, loading, logout, withAuth } = useAdminAuth();
  const [data, setData] = useState({ rows: [], totals: null });
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.get(`/admin/campaigns?days=${days}`, withAuth());
      setData(data);
    } catch {
      toast.error("Failed to load campaigns.");
    } finally {
      setBusy(false);
    }
  }, [days, withAuth]);

  useEffect(() => { if (admin) fetchData(); }, [admin, fetchData]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center font-serif text-2xl">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const t = data.totals || { clicks: 0, checkouts_started: 0, paid_orders: 0, paid_revenue_cents: 0, conversion_rate: 0 };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="eyebrow">Internal</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2">Campaign Attribution</h1>
          <p className="text-[#6B5757] mt-2 text-sm">
            Paid-media performance keyed by <span className="font-mono">utm_campaign</span>.
            Last-touch attribution across a {days}-day window.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white border border-[#E5CDBB] rounded-md p-1">
            {RANGES.map((r) => (
              <button
                key={r.value}
                data-testid={`campaigns-range-${r.value}`}
                onClick={() => setDays(r.value)}
                className={`px-3 py-1 text-xs font-mono uppercase tracking-widest rounded ${
                  days === r.value ? "bg-[#2A1F1F] text-[#FBF6F1]" : "text-[#4A3535] hover:bg-[#FDF3ED]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            data-testid="campaigns-refresh-btn"
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 border border-[#E5CDBB] rounded-md text-sm hover:bg-[#FDF3ED]"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            data-testid="campaigns-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 border border-[#E5CDBB] rounded-md text-sm hover:bg-[#FDF3ED]"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap">
        <Link data-testid="admin-nav-orders" to="/admin/orders" className="text-xs font-mono uppercase tracking-widest text-[#6B5757] hover:text-[#2A1F1F] px-3 py-1.5 border border-[#E5CDBB] rounded">Orders</Link>
        <span className="text-xs font-mono uppercase tracking-widest text-[#2A1F1F] px-3 py-1.5 bg-[#2A1F1F] text-[#FBF6F1] rounded">Campaigns</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard icon={MousePointerClick} label="Clicks" value={t.clicks.toLocaleString()} testid="campaigns-total-clicks" />
        <StatCard icon={ShoppingBag} label="Checkouts" value={t.checkouts_started.toLocaleString()} testid="campaigns-total-checkouts" />
        <StatCard icon={TrendingUp} label="Paid orders" value={t.paid_orders.toLocaleString()} testid="campaigns-total-orders" />
        <StatCard icon={DollarSign} label="Attributed revenue" value={formatUsd(t.paid_revenue_cents)} testid="campaigns-total-revenue" />
      </div>

      {busy && data.rows.length === 0 ? (
        <div className="py-16 text-center text-[#6B5757] text-sm">Loading campaigns…</div>
      ) : data.rows.length === 0 ? (
        <div data-testid="campaigns-empty" className="py-20 text-center border border-dashed border-[#E5CDBB] rounded-lg bg-white">
          <div className="eyebrow">No data yet</div>
          <p className="font-serif text-2xl mt-4 text-[#2A1F1F]">Send traffic to <span className="font-mono text-lg">/go/liposuction?utm_campaign=…</span></p>
          <p className="text-sm text-[#6B5757] mt-3">Clicks will appear here as soon as they land.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#F0DFD0] rounded-lg bg-white">
          <table data-testid="campaigns-table" className="min-w-full">
            <thead className="bg-[#FDF3ED]">
              <tr>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Campaign</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Source · Medium</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Clicks</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Checkouts</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Paid</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Revenue</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Conv %</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} data-testid={`campaigns-row-${r.utm_campaign}`} className="border-t border-[#F0DFD0]">
                  <td className="px-4 py-4 align-top">
                    <div className="font-mono text-sm text-[#2A1F1F]">{r.utm_campaign}</div>
                    {r.landing_paths && r.landing_paths.length > 0 && (
                      <div className="text-[10px] text-[#6B5757] mt-1 space-y-0.5">
                        {r.landing_paths.slice(0, 3).map((p, j) => (
                          <div key={j} className="font-mono truncate max-w-[220px]">{p}</div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="text-sm text-[#4A3535]">{r.utm_source}</div>
                    <div className="text-[10px] text-[#6B5757] font-mono uppercase tracking-widest">{r.utm_medium}</div>
                  </td>
                  <td className="px-4 py-4 align-top text-right font-mono text-sm">{r.clicks.toLocaleString()}</td>
                  <td className="px-4 py-4 align-top text-right font-mono text-sm">{r.checkouts_started.toLocaleString()}</td>
                  <td className="px-4 py-4 align-top text-right font-mono text-sm">{r.paid_orders.toLocaleString()}</td>
                  <td className="px-4 py-4 align-top text-right font-mono text-sm">{formatUsd(r.paid_revenue_cents)}</td>
                  <td className="px-4 py-4 align-top text-right font-mono text-sm">{r.conversion_rate.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-10 text-xs text-[#6B5757]">
        <Link to="/" className="hover:text-[#2A1F1F]">← Back to storefront</Link>
      </div>
    </div>
  );
}
